import { ErrorOutline, HourglassEmpty } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { BASE_COLORS } from 'lib/functions/const/colors';
import React from 'react';

import { PulsingDot, StatusChip } from './atoms';
import NodeTooltipContent from './NodeTooltipContent';
import { isNodeFailed, isNodeWaiting, NodeResult } from './types';

type NodeStatus = 'waiting' | 'ready' | 'failed' | 'completed';

const STATUS_STYLE: Record<
  NodeStatus,
  { border: string; bg: string; circleColor: string; circleText: string }
> = {
  waiting: {
    border: `1px solid ${BASE_COLORS.amber}55`,
    bg: `${BASE_COLORS.amber}0D`,
    circleColor: `${BASE_COLORS.amber}22`,
    circleText: BASE_COLORS.amber,
  },
  ready: {
    border: `1px solid ${BASE_COLORS.green}55`,
    bg: `${BASE_COLORS.green}0D`,
    circleColor: `${BASE_COLORS.green}22`,
    circleText: BASE_COLORS.green,
  },
  failed: {
    border: `1px solid ${BASE_COLORS.red}55`,
    bg: `${BASE_COLORS.red}0D`,
    circleColor: `${BASE_COLORS.red}22`,
    circleText: BASE_COLORS.red,
  },
  completed: {
    border: '1px solid #e8e8e8',
    bg: '#fafafa',
    circleColor: BASE_COLORS.blue,
    circleText: BASE_COLORS.white,
  },
};

const getNodeStatus = (node: NodeResult): NodeStatus => {
  if (isNodeWaiting(node)) return 'waiting';
  if (isNodeFailed(node)) return 'failed';
  if (!node.statusNode) return 'ready';
  return 'completed';
};

const NodeCircle: React.FC<{ index: number; status: NodeStatus }> = ({
  index,
  status,
}) => {
  const { circleColor, circleText } = STATUS_STYLE[status];
  return (
    <Box
      sx={{
        backgroundColor: circleColor,
        color: circleText,
        borderRadius: '50%',
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontWeight: 700,
        fontSize: '0.65rem',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>
        {index + 1}
      </Typography>
    </Box>
  );
};

const NodeRow: React.FC<{
  node: NodeResult;
  index: number;
  total: number;
}> = ({ node, index, total }) => {
  const status = getNodeStatus(node);
  const { border, bg } = STATUS_STYLE[status];

  const rowContent = (
    <Box
      sx={{
        p: 1,
        border,
        borderRadius: 1,
        backgroundColor: bg,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: status === 'completed' ? 'pointer' : 'default',
        '&:hover':
          status === 'completed' ? { backgroundColor: '#f0f0f0' } : undefined,
      }}
    >
      <NodeCircle index={index} status={status} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '0.8rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {node.nodeName || 'Sin nombre'}
        </Typography>

        {status === 'waiting' && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}
          >
            <HourglassEmpty
              sx={{ fontSize: '0.7rem', color: BASE_COLORS.amber }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 500,
                color: BASE_COLORS.amber,
              }}
            >
              {node.resultText || 'Esperando información...'}
            </Typography>
          </Box>
        )}

        {status === 'ready' && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}
          >
            <HourglassEmpty
              sx={{ fontSize: '0.7rem', color: BASE_COLORS.green }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 500,
                color: BASE_COLORS.green,
              }}
            >
              {node.resultText}
            </Typography>
          </Box>
        )}

        {status === 'failed' && node.resultText && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}
          >
            <ErrorOutline sx={{ fontSize: '0.7rem', color: BASE_COLORS.red }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 500,
                color: BASE_COLORS.red,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {node.resultText}
            </Typography>
          </Box>
        )}

        {status === 'completed' && node.resultText && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.68rem',
              color: BASE_COLORS.gray,
              fontStyle: 'italic',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {node.resultText}
          </Typography>
        )}
      </Box>

      {status === 'waiting' && <PulsingDot delay={(index / total) * 0.6} />}

      {status === 'ready' && (
        <PulsingDot delay={(index / total) * 0.6} color={BASE_COLORS.green} />
      )}

      {status === 'failed' && (
        <ErrorOutline
          sx={{ fontSize: '1rem', color: BASE_COLORS.red, flexShrink: 0 }}
        />
      )}

      {status === 'completed' &&
        node.statusNode?.name &&
        node.statusNode?.severity !== undefined && (
          <StatusChip
            label={node.statusNode.name}
            severity={node.statusNode.severity}
          />
        )}
    </Box>
  );

  if (status === 'completed') {
    return (
      <LightTooltip
        title={<NodeTooltipContent node={node} />}
        placement="right"
      >
        {rowContent}
      </LightTooltip>
    );
  }

  return rowContent;
};

interface NodesSectionProps {
  nodes: NodeResult[];
  hasWaiting?: boolean;
  hideTitle?: boolean;
}

const NodesSection: React.FC<NodesSectionProps> = ({
  nodes,
  hideTitle = false,
}) => (
  <Box>
    {!hideTitle && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Nodos de evaluación
        </Typography>
      </Box>
    )}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {nodes.map((node, index) => (
        <NodeRow key={index} node={node} index={index} total={nodes.length} />
      ))}
    </Box>
  </Box>
);

export default NodesSection;
